import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
// @ts-expect-error — type 미포함
import Editor from '@toast-ui/editor'
import '@toast-ui/editor/dist/toastui-editor.css'
import { apiPut } from '../lib/api'
import { useDocumentTabs } from '../lib/useDocumentTabs'
import { useToast } from './Toast'
import type { DocumentDto } from '../lib/types'

interface EditorPaneProps {
  doc: DocumentDto
  /** save 또는 cancel 후 호출 — caller 가 view 모드로 전환 (search param 제거) */
  onLeaveEdit: () => void
}

/**
 * 인라인 edit pane — DocumentView 안에서 ?edit=1 시 노출.
 * Toast UI Editor mount, save 시 PUT /api/documents/{uuid}.
 * 변경 발생 → useDocumentTabs.setDirty(uuid, true), save/cancel 시 false.
 */
export function EditorPane({ doc, onLeaveEdit }: EditorPaneProps) {
  const qc = useQueryClient()
  const tabs = useDocumentTabs()
  const toast = useToast()
  const editorRef = useRef<HTMLDivElement>(null)
  const editorInstanceRef = useRef<{ destroy: () => void; getMarkdown: () => string } | null>(null)
  const originalContentRef = useRef<string>(doc.content ?? '')
  const [title, setTitle] = useState(doc.title)
  const [busy, setBusy] = useState(false)
  const [dirty, setDirtyLocal] = useState(false)

  // editor mount — 한 번만. doc.uuid 가 바뀌면 destroy 후 재mount (실제 발생 안 함, prop 새로 들어오면 컴포넌트 자체 재mount 권장)
  useEffect(() => {
    if (!editorRef.current || editorInstanceRef.current) return
    const e = new Editor({
      el: editorRef.current,
      height: '100%',
      initialEditType: 'markdown',
      previewStyle: 'vertical',
      initialValue: doc.content ?? '',
      usageStatistics: false,
      events: {
        change: () => {
          const cur = e.getMarkdown()
          const next = cur !== originalContentRef.current || title !== doc.title
          setDirtyLocal(next)
          tabs.setDirty(doc.uuid, next)
        },
      },
    })
    editorInstanceRef.current = e
    return () => {
      e.destroy()
      editorInstanceRef.current = null
      // 떠날 때 dirty 초기화 (caller 가 보존이 필요하면 이 cleanup 도중에 제어 가능)
      tabs.setDirty(doc.uuid, false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.uuid])

  // title 변경도 dirty 트리거
  useEffect(() => {
    const editor = editorInstanceRef.current
    const cur = editor?.getMarkdown() ?? originalContentRef.current
    const next = cur !== originalContentRef.current || title !== doc.title
    setDirtyLocal(next)
    tabs.setDirty(doc.uuid, next)
  }, [title, doc.title, doc.uuid])  // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!editorInstanceRef.current) return
    const content = editorInstanceRef.current.getMarkdown()
    setBusy(true)
    try {
      await apiPut(`/api/documents/${doc.uuid}`, { title, content })
      originalContentRef.current = content
      tabs.setDirty(doc.uuid, false)
      setDirtyLocal(false)
      // 캐시 무효화 — view 모드 전환 시 새 content 표시
      qc.invalidateQueries({ queryKey: ['documents', doc.uuid] })
      qc.invalidateQueries({ queryKey: ['projects', doc.projectUuid, 'documents'] })
      toast.show('Saved')
      onLeaveEdit()
    } catch (e) {
      toast.show('Save failed')
      console.error('save failed', e)
    } finally {
      setBusy(false)
    }
  }

  const cancel = () => {
    if (dirty && !window.confirm('저장되지 않은 변경이 있습니다. 취소할까요?')) return
    tabs.setDirty(doc.uuid, false)
    onLeaveEdit()
  }

  // dirty 시 페이지 이탈 차단 (브라우저 reload/close)
  useEffect(() => {
    if (!dirty) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty])

  return (
    <div className="editor-pane" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="editor-pane-toolbar" style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 16px', borderBottom: '1px solid var(--border-subtle)',
        background: 'var(--bg-sidebar)',
      }}>
        <input id="doc-title-edit" type="text" value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
          style={{
            flex: 1, padding: '6px 10px', fontSize: 16, fontWeight: 500,
            border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-sm)',
            background: 'var(--bg-card)', color: 'var(--text)',
          }} />
        <button id="btn-editor-save" type="button" className="btn btn-primary btn-sm"
                disabled={busy} onClick={save}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button id="btn-editor-cancel" type="button" className="btn btn-ghost btn-sm"
                disabled={busy} onClick={cancel}>
          Cancel
        </button>
      </div>
      <div ref={editorRef} className="editor-pane-body" style={{ flex: 1, minHeight: 0 }} />
    </div>
  )
}
